import { useInfiniteQuery, useMutation, useQuery, useSuspenseInfiniteQuery } from "@tanstack/react-query";

import FeedRequests from "@/api/feed/feed.requests.ts";
import { useLang } from "@/hooks/useLang.ts";

export const FEED_KEYS = {
  contentHistory: (lang: string, limit: number) => ["content-hint", lang, limit].filter(Boolean),
  contentCommentsList: (lang: string, limit: number, id: number) => ["comments-list", lang, limit, id].filter(Boolean),
  contentDetailHistory: (lang: string, id: number) => ["content-history-detail", lang, id].filter(Boolean),
  favorites: (lang: string, limit: number) => ["favorites", lang, limit].filter(Boolean),
};

export const useGetContentHistory = (limit: number) => {
  const lang = useLang();

  return useSuspenseInfiniteQuery({
    queryKey: FEED_KEYS.contentHistory(lang, limit),
    queryFn: ({ pageParam = 0 }) => FeedRequests.getContentHistory(limit, pageParam * limit),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetCommentsList = (id: number, limit: number) => {
  const lang = useLang();

  return useSuspenseInfiniteQuery({
    queryKey: FEED_KEYS.contentCommentsList(lang, limit, id),
    queryFn: ({ pageParam = 0 }) => FeedRequests.getCommentList(limit, pageParam * limit, id),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetEpisodeHistory = (id: number, enabled: boolean = true) => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token");

  return useQuery({
    queryKey: FEED_KEYS.contentDetailHistory(lang, id),
    queryFn: () => FeedRequests.getEpisodeHistory(id),
    staleTime: 1000 * 60 * 15,
    enabled: !!token && enabled,
  });
};

export const useUpdateEpisodeHistory = () => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => FeedRequests.postEpisodeHistory({ formData }),
  });
};

export const useCreateComment = () => {
  return useMutation({
    mutationFn: ({ ...args }: { id: number; rating: number; text: string }) => FeedRequests.createComment(args),
  });
};

export const usePostSupportMessage = () => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) =>
      FeedRequests.postSupportMessage({ formData }).then((res) => res),
  });
};

export const useCreateFavoriteContent = () => {
  return useMutation({
    mutationFn: (id: number) => FeedRequests.createFavoriteContent(id),
  });
};

export const useDeleteFavoriteContent = () => {
  return useMutation({
    mutationFn: (id: number) => FeedRequests.deleteFavoriteContent(id),
  });
};

export const useCreateNotRecContent = () => {
  return useMutation({
    mutationFn: (id: number) => FeedRequests.createNotRecContent(id),
  });
};

export const useDeleteNotRecContent = () => {
  return useMutation({
    mutationFn: (id: number) => FeedRequests.deleteNotRecContent(id),
  });
};

export const useGetFavorites = (limit: number) => {
  const lang = useLang();

  return useInfiniteQuery({
    queryKey: FEED_KEYS.favorites(lang, limit),
    queryFn: ({ pageParam = 0 }) => FeedRequests.getFavorites(limit, pageParam * limit),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};
